import type { ProjectSummary, SavedProject } from '@/state/projects/project';

/**
 * Saved projects in IndexedDB (it keeps plain data and small pictures in
 * every browser, from windows and workers alike). Whole projects and the
 * light summaries the home page lists live in two stores written together,
 * so the list never reads every project's tracks and captions.
 */
const NAME = 'shycut-projects';
const VERSION = 1;
const PROJECTS = 'projects';
const SUMMARIES = 'summaries';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB is not available'));
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PROJECTS)) db.createObjectStore(PROJECTS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(SUMMARIES)) db.createObjectStore(SUMMARIES, { keyPath: 'id' });
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another tab upgrading the database closes this connection; the next call opens a fresh one.
      db.onversionchange = () => {
        db.close();
        opening = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      opening = null;
      reject(request.error ?? new Error('Could not open saved projects'));
    };
  });
  return opening;
}

const done = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Saving failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Saving was cancelled'));
  });

const result = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Reading failed'));
  });

export const summaryOf = ({ id, name, createdAt, updatedAt, durationSec, clipCount, poster, recordingId }: SavedProject): ProjectSummary => ({
  id,
  name,
  createdAt,
  updatedAt,
  durationSec,
  clipCount,
  poster,
  recordingId,
});

export const projectDb = {
  async save(project: SavedProject): Promise<void> {
    const db = await open();
    const transaction = db.transaction([PROJECTS, SUMMARIES], 'readwrite');
    transaction.objectStore(PROJECTS).put(project);
    transaction.objectStore(SUMMARIES).put(summaryOf(project));
    await done(transaction);
  },

  async get(id: string): Promise<SavedProject | null> {
    const db = await open();
    return ((await result(db.transaction(PROJECTS).objectStore(PROJECTS).get(id))) as SavedProject | undefined) ?? null;
  },

  /** Newest first. */
  async list(): Promise<ProjectSummary[]> {
    const db = await open();
    const all = (await result(db.transaction(SUMMARIES).objectStore(SUMMARIES).getAll())) as ProjectSummary[];
    return all.sort((a, b) => b.updatedAt - a.updatedAt);
  },

  async remove(id: string): Promise<void> {
    const db = await open();
    const transaction = db.transaction([PROJECTS, SUMMARIES], 'readwrite');
    transaction.objectStore(PROJECTS).delete(id);
    transaction.objectStore(SUMMARIES).delete(id);
    await done(transaction);
  },
};

export type ProjectDb = typeof projectDb;
